import CloudEvent from './CloudEvent'
import { queryClient } from 'core/queryClient'
import { logCustomEvent } from '@braze/web-sdk'
import { ID, UserProfile } from 'types/entity'

// Global window object extensions for analytics libraries
declare global {
  interface Window {
    mixpanel: {
      alias: (userId: string) => void
      identify: (userId: string) => void
      track: (
        eventName: string,
        properties?: Record<string, unknown>,
        callback?: (status: number) => void
      ) => void
      people: {
        set: (properties: Record<string, unknown>) => void
      }
      reset: () => void
    }
    appboy: {
      changeUser: (userId: string) => void
      getUser: () => {
        setFirstName: (firstName: string) => void
        setLastName: (lastName: string) => void
        setEmail: (email: string) => void
        setPhoneNumber: (phone: string) => void
        setCustomUserAttribute: (
          key: string,
          value: string | number | boolean | unknown[]
        ) => void
      }
    }
    profitwell: (action: string, value?: string) => void
  }
}

const appProps: Record<string, unknown> = {
  'Dashboard Version': '1.4.9.9',
  visitedDashboard: true
}

const shouldSkip = (): boolean => {
  return window.location.host === 'localhost:6006'
}

const brazeEvents: string[] = [
  CloudEvent.DataExported,
  CloudEvent.ClickedApproveReport,
  CloudEvent.ClickedHRLearnMore,
  CloudEvent.CreateTeam,
  CloudEvent.ClickedRulesFilter
]

const singularEvents: string[] = [
  'sng_login', // REQ-EVC-2253-017
  'sng_complete_registration', // REQ-EVC-2253-003 / REQ-EVC-2253-015
  'persona_selected',
  'income_source_selected',
  'sng_add_payment_info', // SLICE-003 · REQ-007
  'trip_created', // TICKET-011 · EVC-2790
  'expense_created' // TICKET-011 · EVC-2790
]

const brazeProperties: string[] = [
  CloudEvent.Accountant,
  CloudEvent.FavoritePlaces,
  CloudEvent.FavoriteTransactions,
  CloudEvent.FavoriteTrips,
  CloudEvent.FreeTripsRemaining,
  CloudEvent.IncomeSources,
  CloudEvent.InstitutionUserCount,
  CloudEvent.MonthlyReportEnabled,
  CloudEvent.Persona,
  CloudEvent.Premium,
  CloudEvent.TotalUnclassifiedTrips,
  CloudEvent.TrialDaysRemaining,
  CloudEvent.VerificationStatus,
  CloudEvent.TeamRoles,
  CloudEvent.ExternalId,
  CloudEvent.IsEverlanceAdminUser
]

const brazePredefinedMethodProperties: string[] = [
  CloudEvent.FirstName,
  CloudEvent.LastName,
  CloudEvent.Email,
  CloudEvent.Phone
]

const filterBrazeProperties = (properties: Record<string, unknown>): void => {
  for (const propName in properties) {
    const propVal = properties[propName]
    if (brazePredefinedMethodProperties.includes(propName)) {
      switch (propName) {
        case '$first_name':
          window.appboy.getUser().setFirstName(propVal as string)
          break
        case '$last_name':
          window.appboy.getUser().setLastName(propVal as string)
          break
        case '$email':
          window.appboy.getUser().setEmail(propVal as string)
          break
        case '$phone':
          window.appboy.getUser().setPhoneNumber(propVal as string)
          break
        default:
          break
      }
    } else if (brazeProperties.includes(propName)) {
      switch (typeof properties[propName]) {
        case 'string':
        case 'number':
        case 'boolean':
          window.appboy
            .getUser()
            .setCustomUserAttribute(
              propName,
              propVal as string | number | boolean
            )
          break
        case 'object': // Arrays AND null
          if (propVal !== null) {
            window.appboy
              .getUser()
              .setCustomUserAttribute(propName, propVal as unknown[])
          }
          break
        default:
          break
      }
    }
  }
}

const checkForChangesInProperties = (
  serverProps: Record<string, unknown>,
  localProps: Record<string, unknown>,
  token_id: string
): Record<string, unknown> => {
  const changedProps: string[] = []
  for (const propName in serverProps) {
    if (localProps[propName] === undefined) {
      // if prop not in local storage, add it to changed props
      changedProps.push(propName)
    } else if (serverProps[propName] !== localProps[propName]) {
      // if prop value changed, add it to changed props
      if (typeof serverProps[propName] === 'object') {
        if (
          JSON.stringify(serverProps[propName]) !==
            JSON.stringify(localProps[propName]) &&
          serverProps[propName] !== null
        ) {
          changedProps.push(propName)
        }
      } else {
        changedProps.push(propName)
      }
    }
  }
  const propsObj: Record<string, unknown> = {}
  for (const propName of changedProps) {
    if (propName !== 'undefined') {
      propsObj[propName] = serverProps[propName]
    }
  }
  localStorage.setItem(
    `braze-${token_id}`,
    JSON.stringify({ ...serverProps, ...propsObj })
  )
  return propsObj
}

export default class AnalyticsHelper {
  static alias(user: UserProfile): void {
    if (shouldSkip()) return

    window.mixpanel.alias(String(user.token_id))
    window.appboy.changeUser(String(user.token_id))
    // // SENTRY
  }

  static identify(user: UserProfile): void {
    if (shouldSkip()) return

    window.mixpanel.identify(user.token_id as string)
    window.appboy.changeUser(user.token_id as string)

    // // SENTRY

    window.profitwell('user_email', user.email)
    window.profitwell('cq_get')
  }

  static trackEvent(
    event_name: string,
    properties: Record<string, unknown> = {}
  ): void {
    if (shouldSkip()) return
    window.mixpanel.track(event_name, properties, (mpstatus) => {
      mpstatus === 0 ? null : console.log(/* { mpstatus, event } */)
    })

    const userData = queryClient.getQueryData(['users', 'me']) as
      UserProfile | undefined

    const newProperties: Record<string, unknown> = {
      ...properties,
      is_everlance_admin_user: !!userData?.is_everlance_admin_user
    }

    if (brazeEvents.includes(event_name)) {
      if (
        event_name === CloudEvent.ClickedApproveReport &&
        properties.channel === 'report detail'
      ) {
        return
      }
      logCustomEvent?.(event_name, newProperties)
    }

    const isB2CUser = !userData?.teams?.length
    if (singularEvents.includes(event_name) && isB2CUser) {
      try {
        window.singularSdk.event(event_name, properties)
      } catch {
        /* fail-silent — a Singular event error must not affect Mixpanel/Braze delivery */
      }
    }
  }

  static addPeopleProperty(
    token_id: ID | string,
    serverProps: Record<string, unknown>,
    loginOrRefresh = false
  ): void {
    const tokenId = token_id as string
    if (shouldSkip()) return
    // identify must be called along with people.set
    window.mixpanel.identify(tokenId)
    const props = { ...serverProps, ...appProps }
    window.mixpanel.people.set(props)

    // Braze Properties
    window.appboy.changeUser(tokenId)
    const localProps = localStorage.getItem(`braze-${tokenId}`)
    if (loginOrRefresh) {
      if (!localProps) {
        // if first login, set all properties
        filterBrazeProperties(serverProps)
      }
      // if login or refresh, store the user properties in local storage
      localStorage.setItem(`braze-${tokenId}`, JSON.stringify(serverProps))
    }
    // check if the user has braze properties in local storage
    if (localProps) {
      // look for changes between local and server properties
      const localPropsObj = JSON.parse(localProps) as Record<string, unknown>
      const updatedProps = checkForChangesInProperties(
        serverProps,
        localPropsObj,
        tokenId
      )
      filterBrazeProperties(updatedProps)
    }
  }

  static clearUserCookie(): void {
    if (shouldSkip()) return
    window.mixpanel.reset()

    // Singular device→customer identity teardown (REQ-EVC-2253-017).
    // Deliberately NOT flag-gated: an identity bound while the flag was on must
    // still be torn down if the flag was flipped off mid-session.
    try {
      window.singularSdk?.logout()
    } catch {
      // fail-silent — a Singular teardown error must not affect the other
      // destinations
    }

    // // SENTRY
  }
}

// TODO
//     Raven.wrap({
//   tags: {git_commit: 'c0deb10c4'}
// }, function () { /* ... */ });
